/**
 * 그룹 삭제 확인 모달 컴포넌트 (admin만)
 * - 삭제 확인 메시지 표시
 * - 그룹 정보 표시
 * - 소속 사용자가 있으면 삭제 불가 (Backend에서 체크)
 */

import React, { useState } from 'react';
import { Modal, Typography, Space, message } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { groupService } from '../../../services/groupService';
import type { Group } from '../../../types/group';

const { Text } = Typography;

interface GroupDeleteConfirmProps {
  open: boolean;
  group: Group | null;
  onClose: () => void;
  onSuccess: () => void;
}

export const GroupDeleteConfirm: React.FC<GroupDeleteConfirmProps> = ({
  open,
  group,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);

  /**
   * 그룹 삭제 처리 (Soft Delete)
   */
  const handleDelete = async () => {
    if (!group) return;

    try {
      setLoading(true);
      const result = await groupService.deleteGroup(group.id);
      message.success(result.message || '그룹이 삭제되었습니다');
      onClose();
      onSuccess();
    } catch (error: any) {
      if (error.response?.data?.detail) {
        // Backend에서 반환한 에러 메시지 (예: "소속 사용자가 있는 그룹은 삭제할 수 없습니다")
        message.error(error.response.data.detail);
      } else {
        message.error('그룹 삭제 실패');
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <Space>
          <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
          그룹 삭제
        </Space>
      }
      open={open}
      onOk={handleDelete}
      onCancel={onClose}
      confirmLoading={loading}
      okText="삭제"
      cancelText="취소"
      okButtonProps={{ danger: true }}
      width={500}
    >
      {group && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Text>다음 그룹을 정말 삭제하시겠습니까?</Text>

          <div
            style={{
              padding: '16px',
              background: '#f5f5f5',
              borderRadius: '4px',
            }}
          >
            <Space direction="vertical" size="small">
              <div>
                <Text strong>그룹명:</Text> <Text>{group.name}</Text>
              </div>
              <div>
                <Text strong>그룹 코드:</Text> <Text>{group.code}</Text>
              </div>
              {group.description && (
                <div>
                  <Text strong>설명:</Text> <Text>{group.description}</Text>
                </div>
              )}
              <div>
                <Text strong>상태:</Text>{' '}
                <Text>{group.is_active ? '✅ 활성' : '❌ 비활성'}</Text>
              </div>
            </Space>
          </div>

          <Text type="danger" strong>
            이 작업은 되돌릴 수 없습니다. (Soft Delete 방식)
          </Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            * 소속 사용자가 있는 그룹은 삭제할 수 없습니다.
          </Text>
        </Space>
      )}
    </Modal>
  );
};
